/**
 * «Compila dal documento» sulla nuova scheda (lettura con l'AI, 31/10/2026): regole senza dipendenze
 * dal browser. Dalla proposta dell'API (POST /persone/lettura-documento) alle righe della tabella
 * «Dal documento → Nella scheda», e dalle righe scelte al modulo.
 *
 * Le regole:
 *   - una riga per dato del modulo; paese e luogo di nascita stanno insieme, perche' il comune
 *     italiano e la localita' estera dipendono dal paese (D-12);
 *   - sono gia' spuntate le righe il cui campo nella scheda e' vuoto; non lo sono quelle che
 *     l'operatore ha gia' scritto, in modo diverso o uguale;
 *   - la condizione giuridica compare solo se chi crea la scheda la puo' scrivere sulla sede scelta
 *     e la persona non rappresenta un ente;
 *   - i campi riempiti si ricordano, per evidenziarli finche' l'operatore non li tocca.
 *
 * Come regole-anagrafica.ts, il modulo gira anche nelle prove dell'API
 * (apps/api/test/lettura-documento.test.ts).
 */

import { comuneVuoto, type StatiScheda } from './regole-anagrafica.ts'

/** Campi proposti dall'API: i nomi del corpo di POST /persone (apps/api/src/servizi/lettura-documento.ts). */
export type CampoProposto =
  | 'cognome' | 'nome' | 'genere_codice' | 'data_nascita' | 'paese_nascita' | 'comune_nascita'
  | 'luogo_nascita_estero' | 'cittadinanza' | 'cf' | 'condizione_giuridica' | 'motivo_permesso'
  | 'permesso_scadenza' | 'tipo_documento' | 'numero_documento'

export type Origine = 'modello' | 'mrz' | 'codice_fiscale'

export type Proposta = {
  valore: string
  testo: string
  origine: Origine
  confidenza: number | null
  avvisi: string[]
  provincia?: string
}

export type TipoLetto = 'passaporto' | 'carta_identita' | 'permesso_soggiorno' | 'tessera_sanitaria' | 'patente' | 'altro'

/** Risposta di POST /persone/lettura-documento. */
export type LetturaProposta = {
  modello: string
  tipo_letto: TipoLetto
  documento: { data_rilascio: string | null; data_scadenza: string | null; ente_rilascio: string | null }
  mrz: { formato: 'TD1' | 'TD2' | 'TD3'; valida: boolean; errori: string[] } | null
  campi: Partial<Record<CampoProposto, Proposta>>
  avvisi: string[]
}

/** Le righe della tabella, nell'ordine del modulo. */
export type ChiaveVoce =
  | 'cognome' | 'nome' | 'genere' | 'nascita' | 'luogo' | 'cittadinanza' | 'cf'
  | 'condizione' | 'motivo' | 'scadenza_permesso'

export type VoceLettura = {
  chiave: ChiaveVoce
  etichetta: string
  dalDocumento: string
  /** Il valore nel modulo, come si legge; vuoto se il campo e' vuoto. */
  nellaScheda: string
  /** Il modulo ha gia' lo stesso valore: non c'e' niente da cambiare. */
  uguale: boolean
  /** Proposta iniziale: spuntata se il campo nel modulo e' vuoto. */
  spuntata: boolean
  /** Le proposte che compongono la riga, per l'origine e gli avvisi. */
  proposte: Proposta[]
}

/** Nome di una voce di catalogo: genere, condizione giuridica, motivo; per i paesi il catalogo «paese». */
export type NomeDi = (catalogo: 'genere' | 'paese' | 'condizione_giuridica' | 'motivo_permesso', codice: string) => string

/** gg/mm/aaaa da AAAA-MM-GG. */
const giorno = (iso: string) => (/^\d{4}-\d{2}-\d{2}$/.test(iso) ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : iso)

/** Testo del comune come lo scrive la scelta del comune nel modulo: «RM · Roma». */
export const testoComune = (nome: string, provincia?: string) => (provincia ? `${provincia} · ${nome}` : nome)

/**
 * Le righe della tabella. `conCondizione`: chi crea la scheda puo' scrivere la condizione giuridica
 * sulla sede scelta e la persona non rappresenta un ente; altrimenti quelle righe non ci sono.
 */
export function vociDaLettura(
  lettura: LetturaProposta, stati: StatiScheda, nomeDi: NomeDi, { conCondizione }: { conCondizione: boolean },
): VoceLettura[] {
  const a = stati.anagrafica
  const c = stati.condizione
  const p = lettura.campi
  const voci: VoceLettura[] = []
  const aggiungi = (chiave: ChiaveVoce, etichetta: string, proposte: (Proposta | undefined)[], dalDocumento: string,
    nellaScheda: string, uguale: boolean) => {
    const presenti = proposte.filter((x): x is Proposta => !!x)
    if (!presenti.length) return
    voci.push({ chiave, etichetta, dalDocumento, nellaScheda, uguale, spuntata: nellaScheda === '', proposte: presenti })
  }

  if (p.cognome) aggiungi('cognome', 'Cognome', [p.cognome], p.cognome.testo, a.cognome.trim(), a.cognome.trim() === p.cognome.valore)
  if (p.nome) aggiungi('nome', 'Nome', [p.nome], p.nome.testo, a.nome.trim(), a.nome.trim() === p.nome.valore)
  if (p.genere_codice) {
    aggiungi('genere', 'Genere', [p.genere_codice], nomeDi('genere', p.genere_codice.valore),
      a.genere_codice ? nomeDi('genere', a.genere_codice) : '', a.genere_codice === p.genere_codice.valore)
  }
  if (p.data_nascita) {
    // Il campo e' vuoto se manca il dato del modo scelto: data, anno o fascia d'eta'.
    const scheda = a.modo_nascita === 'data' ? (a.data_nascita ? giorno(a.data_nascita) : '')
      : a.modo_nascita === 'anno' ? (a.anno_nascita ? `solo l’anno: ${a.anno_nascita}` : '')
        : (a.fascia_eta_manuale ? 'non nota' : '')
    aggiungi('nascita', 'Data di nascita', [p.data_nascita], p.data_nascita.testo, scheda,
      a.modo_nascita === 'data' && a.data_nascita === p.data_nascita.valore)
  }
  if (p.paese_nascita) {
    const luogo = p.comune_nascita ?? p.luogo_nascita_estero
    const luogoTesto = p.comune_nascita ? testoComune(p.comune_nascita.testo, p.comune_nascita.provincia)
      : p.luogo_nascita_estero?.testo
    const dalDocumento = [p.paese_nascita.testo, luogoTesto].filter(Boolean).join(', ')
    const luogoScheda = a.comune_nascita.testo || a.luogo_nascita_estero.trim()
    const nellaScheda = [a.paese_nascita ? nomeDi('paese', a.paese_nascita) : '', luogoScheda].filter(Boolean).join(', ')
    const uguale = a.paese_nascita === p.paese_nascita.valore
      && (p.comune_nascita ? a.comune_nascita.codice === p.comune_nascita.valore
        : p.luogo_nascita_estero ? a.luogo_nascita_estero.trim() === p.luogo_nascita_estero.valore : !luogoScheda)
    aggiungi('luogo', 'Paese e luogo di nascita', [p.paese_nascita, luogo], dalDocumento, nellaScheda, uguale)
  }
  if (p.cittadinanza) {
    aggiungi('cittadinanza', 'Cittadinanza', [p.cittadinanza], p.cittadinanza.testo,
      a.cittadinanza ? nomeDi('paese', a.cittadinanza) : '', a.cittadinanza === p.cittadinanza.valore)
  }
  if (p.cf) {
    const cf = a.cf.replace(/\s+/g, '').toUpperCase()
    aggiungi('cf', 'Codice fiscale', [p.cf], p.cf.testo, cf, cf === p.cf.valore)
  }
  if (conCondizione) {
    if (p.condizione_giuridica) {
      aggiungi('condizione', 'Condizione giuridica', [p.condizione_giuridica], p.condizione_giuridica.testo,
        c.condizione_giuridica ? nomeDi('condizione_giuridica', c.condizione_giuridica) : '',
        c.condizione_giuridica === p.condizione_giuridica.valore)
    }
    if (p.motivo_permesso) {
      aggiungi('motivo', 'Motivo del permesso', [p.motivo_permesso], p.motivo_permesso.testo,
        c.motivo_permesso ? nomeDi('motivo_permesso', c.motivo_permesso) : '', c.motivo_permesso === p.motivo_permesso.valore)
    }
    if (p.permesso_scadenza) {
      aggiungi('scadenza_permesso', 'Scadenza del titolo di soggiorno', [p.permesso_scadenza], p.permesso_scadenza.testo,
        c.permesso_scadenza ? giorno(c.permesso_scadenza) : '', c.permesso_scadenza === p.permesso_scadenza.valore)
    }
  }
  return voci
}

/** Le righe spuntate all'inizio: quelle con il campo vuoto nel modulo. */
export const scelteIniziali = (voci: VoceLettura[]): Set<ChiaveVoce> =>
  new Set(voci.filter((v) => v.spuntata).map((v) => v.chiave))

/**
 * Il modulo con i dati delle righe scelte, e i nomi dei campi riempiti, da evidenziare con «Letto dal
 * documento: controlla». Le righe che non hanno una proposta si ignorano.
 */
export function applicaLettura(
  stati: StatiScheda, lettura: LetturaProposta, scelte: ReadonlySet<ChiaveVoce>,
): { stati: StatiScheda; letti: string[] } {
  const a = { ...stati.anagrafica }
  const c = { ...stati.condizione }
  const p = lettura.campi
  const letti: string[] = []
  const scelta = (chiave: ChiaveVoce) => scelte.has(chiave)

  if (scelta('cognome') && p.cognome) { a.cognome = p.cognome.valore; letti.push('cognome') }
  if (scelta('nome') && p.nome) { a.nome = p.nome.valore; letti.push('nome') }
  if (scelta('genere') && p.genere_codice) { a.genere_codice = p.genere_codice.valore; letti.push('genere_codice') }
  if (scelta('nascita') && p.data_nascita) {
    // La data completa: il modo torna «data», e anno e fascia scritti prima si tolgono.
    Object.assign(a, { modo_nascita: 'data', data_nascita: p.data_nascita.valore, anno_nascita: '', fascia_eta_manuale: '' })
    letti.push('data_nascita')
  }
  if (scelta('luogo') && p.paese_nascita) {
    a.paese_nascita = p.paese_nascita.valore
    letti.push('paese_nascita')
    if (p.comune_nascita) {
      a.comune_nascita = {
        codice: p.comune_nascita.valore, testo: testoComune(p.comune_nascita.testo, p.comune_nascita.provincia),
        provincia: p.comune_nascita.provincia ?? '',
      }
      a.luogo_nascita_estero = ''
      letti.push('comune_nascita')
    } else if (p.luogo_nascita_estero) {
      a.comune_nascita = comuneVuoto()
      a.luogo_nascita_estero = p.luogo_nascita_estero.valore
      letti.push('luogo_nascita_estero')
    } else if (a.paese_nascita === 'ITA') {
      // D-12: con l'Italia la localita' estera non vale; con un altro paese non vale il comune.
      a.luogo_nascita_estero = ''
    } else {
      a.comune_nascita = comuneVuoto()
    }
  }
  if (scelta('cittadinanza') && p.cittadinanza) { a.cittadinanza = p.cittadinanza.valore; letti.push('cittadinanza') }
  if (scelta('cf') && p.cf) {
    // Con il codice fiscale il motivo dell'assenza non ha senso (come fa l'API alla creazione).
    Object.assign(a, { cf: p.cf.valore, motivo_assenza_cf_codice: '', motivo_assenza_cf: '' })
    letti.push('cf')
  }
  if (scelta('condizione') && p.condizione_giuridica) {
    c.condizione_giuridica = p.condizione_giuridica.valore
    letti.push('condizione_giuridica')
  }
  if (scelta('motivo') && p.motivo_permesso) { c.motivo_permesso = p.motivo_permesso.valore; letti.push('motivo_permesso') }
  if (scelta('scadenza_permesso') && p.permesso_scadenza) {
    c.permesso_scadenza = p.permesso_scadenza.valore
    letti.push('permesso_scadenza')
  }
  return { stati: { ...stati, anagrafica: a, condizione: c }, letti }
}

/** Come si dice l'origine di un dato, accanto al valore. */
export function origineLeggibile(p: Proposta): string {
  if (p.origine === 'mrz') return 'dalla MRZ, controllata'
  if (p.origine === 'codice_fiscale') return 'dal codice fiscale'
  if (p.confidenza === null) return 'letto dal modello'
  return p.confidenza >= 0.9 ? 'lettura sicura' : p.confidenza >= 0.7 ? 'lettura abbastanza sicura' : 'lettura incerta'
}
